import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";
import { AUTO_HIDE_DURATION, MAX_SNACK, SnackbarsCustomStyle } from "@Layouts/components/Snackbars";
import { RootState } from "@/redux/store";

// Material
import { Box, Stack } from "@mui/material";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import DoneIcon from "@mui/icons-material/Done";

// Components
import CollapsibleLeftPanel from "@/Layouts/components/CollapsibleLeftPanel";
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import PositionTreeIcon from "@/Washouts/components/PositionTreeIcon";
import AppSideBar, { SideBarContent } from "@/Layouts/components/SideBars/AppSideBar";
import Container from "./Container";
import EmptySection from "./EmptySection";

// Hooks
import useAccessPositions from "@/Washouts/hooks/WashoutReview/useAccessPositions";
import { useManagedPositions } from "@/Common/hooks/Positions";
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";

// API
import { getWashoutsPayPeriodTree } from "@/Washouts/api/WashoutReview/queries";
import { ICommissionCycle, IPayPeriod } from "@/Common/api/types/CommissionCycle";

// Helpers
import { getAccessPosition, getCurrentPayPeriod, resolveImage, resolveLoading } from "@/Washouts/helpers/WashoutReview";

// Types
import { IReviewTree, ISelected } from "@/Washouts/types/WashoutReview";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import useApproveRetractWashoutSubscription from "@/Washouts/hooks/WashoutReview/useApproveRetractWashoutSubscription";
import { useChangeRequestSubscriptions } from "@/Washouts/hooks/ChangeRequests/useChangeRequestSubscriptions";
import PositionTreeMenu from "@/Washouts/components/PositionTreeMenu";
import { evaluatePositionStatusConditions, StatusKey } from "@/Washouts/helpers/Statuses";
import { WashoutEllipseMenu } from "@/Washouts/components/WashoutEllipsesMenu";
import { or } from "@/Common/helpers/sonar";
import { IDisabledMenuAction } from "@/Washouts/types/WashoutEllipsesMenu";

type TWrapperProps = {
	onUpdateTitle: (cycle?: ICommissionCycle, payPeriod?: IPayPeriod) => void;
};

const QUERY_IDENTIFIERS = ["washoutReview", "positionsStatusByPayPeriod"];

const getParentCycleId = (payPeriod: IPayPeriod | undefined) => payPeriod?.nextCCId ?? payPeriod?.previousCCId;

const Wrapper = (props: TWrapperProps) => {
	const { onUpdateTitle } = props;
	const { clearCache } = useCacheCleaner();
	const locationPath = useLocation();
	const location = useSelector((state: RootState) => state?.location?.location);
	const user = useSelector((state: RootState) => state.user.user);
	const accessPositions = useAccessPositions();
	const [expanded, setExpanded] = useState([]);
	const [selected, setSelected] = useState<ISelected | null>(null);
	const locationId = location?.locationId ?? "";

	const { data: managedPositions, isLoading: isLoadingPositions } = useManagedPositions({
		queryOpts: {
			staleTime: Infinity,
		},
	});

	const { isLoading: isLoadingCycles, commissionCycle, payPeriod, partnerPayPeriod } = useCommissionCyclesData();

	const { data: reviewsTree, isLoading: isLoadingReviewsTree } = useQuery({
		queryKey: ["washoutReview", "tree", locationId, payPeriod?._id],
		queryFn: async () => {
			if (managedPositions?.length) {
				const payPeriodId = payPeriod?._id ?? "";
				const commissionCycleId = commissionCycle?._id ?? "";
				return (await getWashoutsPayPeriodTree({ commissionCycleId, payPeriodId, locationId })) as IReviewTree[];
			}
			return [];
		},
		enabled: !!managedPositions?.length && !!commissionCycle && !!locationId,
		staleTime: Infinity,
	});

	useApproveRetractWashoutSubscription({
		locationId,
		commissionCycleId: commissionCycle?._id,
		payPeriodId: payPeriod?._id,
		updateWashoutReviewTree: true,
		updateCommissionCycle: true,
		updateWashoutModel: true,
	});

	useApproveRetractWashoutSubscription({
		locationId,
		commissionCycleId: getParentCycleId(payPeriod),
		payPeriodId: payPeriod?._id,
		updateWashoutReviewTree: true,
		updateCommissionCycle: true,
		updateWashoutModel: true,
	});

	useChangeRequestSubscriptions({
		payPeriodId: payPeriod?._id,
		commissionCycleId: locationPath?.state?.commissionCycleId ?? commissionCycle?._id,
	});

	useEffect(() => {
		onUpdateTitle(commissionCycle, payPeriod);
	}, [commissionCycle, payPeriod]);

	useEffect(() => {
		return () => {
			setSelected(null);
		};
	}, [locationId]);

	useEffect(() => {
		return () => {
			clearCache(QUERY_IDENTIFIERS);
		};
	}, []);

	const handleSelection = (selection: ISelected) => {
		if (selection?._id !== selected?._id) {
			setSelected(selection);
		}
		setExpanded([]);
	};

	const washoutConditionsByPosition: Record<string, StatusKey[]> = {};

	const positionStatus = payPeriod?.positionStatus;
	const disabledMenuActionGeneral: IDisabledMenuAction[] = [];
	const mappedTree = reviewsTree?.map((item) => {
		const position = item?.title;
		const unreviewableStatus = item?.unreviewable;
		const positionAccess = getAccessPosition(accessPositions, position);
		const isPositionInvalid = !managedPositions?.includes(position);
		const isAccessInvalid = !positionAccess?.access;
		const disabled = item.disabled || isPositionInvalid || isAccessInvalid || unreviewableStatus;

		const isPendingAction = or(isLoadingCycles, isLoadingPositions, isLoadingReviewsTree);
		const currentPositionStatus = positionStatus?.find((status) => status.position === position);
		const isPositionApproved = currentPositionStatus?.status === "APPROVED";
		const isPostedToPayroll = currentPositionStatus?.status === "POSTED_PAYROLL";
		const shouldDisabledGenerateWashoutMenu = isPositionApproved || isPostedToPayroll;

		const isMandatoryDisabled = or(
			item.disabled ?? false,
			isPendingAction,
			isPostedToPayroll,
			isAccessInvalid,
			isPositionInvalid,
			unreviewableStatus
		);

		const disabledMenuAction = {
			position,
			shouldDisabledGenerateWashoutMenu,
			shouldDisabledApproveWashoutMenu: isMandatoryDisabled,
			shouldDisabledRetractWashoutMenu: isMandatoryDisabled,
		};

		disabledMenuActionGeneral.push(disabledMenuAction);

		return {
			...item,
			disabled,
			title: position,
			list: item?.list
				?.map((element) => {
					const itemProps = {
						currentPayPlanAssignmentId: element?.currentPayPlanAssignmentId ?? "",
						selected: false,
						reviewed: element?.reviewed ?? false,
						hasWashout: element?.hasWashout ?? false,
						upcomingPayPlanAssignmentId: element?.upcomingPayPlanAssignmentId ?? "",
						washoutGenerationError: element?.washoutGenerationError ?? false,
					};

					const condition = evaluatePositionStatusConditions({ itemProps, unReviewable: element?.unreviewable, selected: false });
					if (!condition?.key) {
						return null;
					}

					if (!element.list?.length) {
						washoutConditionsByPosition[position] = [...(washoutConditionsByPosition[position] ?? []), condition?.key];
					}

					return {
						...element,
						icon: {
							component: <PositionTreeIcon iconKey={condition?.key} tooltip={condition?.tooltip} selected={false} />,
						},
						menu: {
							component: (
								<PositionTreeMenu
									locationId={locationId}
									currentUser={user?._id || ""}
									position={position}
									item={element}
									itemProps={itemProps}
									unReviewable={element?.unreviewable}
									disabledMenuAction={disabledMenuAction}
								/>
							),
						},
						information: {
							variant: "outlined",
						},
						list: element?.list
							?.map((innerElement) => {
								const innerItemProps = {
									currentPayPlanAssignmentId: innerElement?.currentPayPlanAssignmentId ?? "",
									selected: false,
									reviewed: innerElement?.reviewed ?? false,
									hasWashout: innerElement?.hasWashout ?? false,
									upcomingPayPlanAssignmentId: innerElement?.upcomingPayPlanAssignmentId ?? "",
									washoutGenerationError: innerElement?.washoutGenerationError ?? false,
								};
								const payPeriodsCondition = evaluatePositionStatusConditions({
									itemProps: innerItemProps,
									unReviewable: innerElement?.unreviewable,
									selected: false,
								});

								if (!payPeriodsCondition?.key) {
									return null;
								}

								washoutConditionsByPosition[position] = [
									...(washoutConditionsByPosition[position] ?? []),
									payPeriodsCondition?.key,
								];

								return {
									...innerElement,
									disabled: disabled || unreviewableStatus || innerElement?.disabled || innerElement?.unreviewable,
									tooltip: innerElement?.title,
									icon: {
										component: (
											<PositionTreeIcon
												iconKey={payPeriodsCondition?.key}
												tooltip={payPeriodsCondition?.tooltip}
												selected={false}
											/>
										),
										unReviewable: innerElement?.unreviewable,
									},
									menu: {
										component: (
											<PositionTreeMenu
												locationId={locationId}
												currentUser={user?._id || ""}
												position={position}
												item={innerElement}
												itemProps={innerItemProps}
												unReviewable={innerElement?.unreviewable}
												disabledMenuAction={disabledMenuAction}
											/>
										),
									},
									information: {
										variant: "outlined",
									},
								};
							})
							.filter((innerItem) => innerItem !== null),
					};
				})
				.filter((reviewItem) => reviewItem !== null),
			icon: {
				disabled,
				rootLevel: true,
				component: <PositionTreeIcon selected={false} />,
				unReviewable: unreviewableStatus,
			},
		};
	});

	return (
		<SnackbarProvider
			maxSnack={MAX_SNACK}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			autoHideDuration={AUTO_HIDE_DURATION}
			iconVariant={{
				error: <ErrorOutlineRoundedIcon fontSize="small" className="mr-2" />,
				success: <DoneIcon fontSize="small" className="mr-2" />,
			}}
			Components={{
				error: SnackbarsCustomStyle,
				success: SnackbarsCustomStyle,
			}}
		>
			{reviewsTree?.length ? (
				<BodyLayer
					floatingContent={
						<CollapsibleLeftPanel
							control={
								<AppSideBar
									sideBarSection={
										<Stack direction="column" className="h-[620px] justify-between pb-6">
											<Box className='[&_ul[role="tree"]]:!max-h-[515px]'>
												<SideBarContent
													searchProps={{
														loading: resolveLoading(isLoadingCycles, isLoadingReviewsTree, isLoadingPositions),
													}}
													sideBarProps={{
														expanded,
														tree: mappedTree,
														loading: resolveLoading(isLoadingCycles, isLoadingReviewsTree, isLoadingPositions),
														selected: selected?._id ?? "",
														onSelectAction: handleSelection,
													}}
												/>
											</Box>
											<Box className="w-full flex justify-end px-8">
												<WashoutEllipseMenu
													input={{
														locationId,
														commissionCycle,
														payPeriod,
														washoutConditionsByPosition,
														currentUser: user?._id || "",
														partnerPayPeriod: partnerPayPeriod || undefined,
													}}
													disabledMenuAction={disabledMenuActionGeneral}
												/>
											</Box>
										</Stack>
									}
								/>
							}
						/>
					}
				>
					<Container
						locationId={locationId}
						selected={selected}
						currentCommissionCycleId={locationPath?.state?.commissionCycleId ?? commissionCycle?._id}
						currentPayPeriod={getCurrentPayPeriod(commissionCycle?.payPeriods, locationPath.state?.payPeriodId)}
						accessPositions={accessPositions}
					/>
				</BodyLayer>
			) : (
				<BodyLayer>
					<EmptySection image={resolveImage(selected)} title="Washout review is not available for this store" />
				</BodyLayer>
			)}
		</SnackbarProvider>
	);
};

export default Wrapper;
